/**
 * Timestep Header Component
 *
 * Displays timestep metadata and navigation controls.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Group, Button, Text, Badge, Stack } from "@mantine/core";
import {
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import type { TimestepData, NavigationInfo } from "../../types";

interface TimestepHeaderProps {
  timestep: TimestepData;
  navigation: NavigationInfo;
  returnId: string;
}

export const TimestepHeader: React.FC<TimestepHeaderProps> = ({
  timestep,
  navigation,
  returnId,
}) => {
  const navigate = useNavigate();

  const handlePrevious = () => {
    if (navigation.prevTimestepId) {
      navigate(
        `/admin/returns/${returnId}/timesteps/${navigation.prevTimestepId}`
      );
    }
  };

  const handleNext = () => {
    if (navigation.nextTimestepId) {
      navigate(
        `/admin/returns/${returnId}/timesteps/${navigation.nextTimestepId}`
      );
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(`/admin/returns/${returnId}`)}
        >
          Back to Return Debug
        </Button>

        <Group>
          <Button
            variant="light"
            leftSection={<IconChevronLeft size={16} />}
            onClick={handlePrevious}
            disabled={!navigation.prevTimestepId}
          >
            Previous
          </Button>
          <Badge size="lg" variant="light">
            {navigation.currentIndex} of {navigation.totalTimesteps}
          </Badge>
          <Button
            variant="light"
            rightSection={<IconChevronRight size={16} />}
            onClick={handleNext}
            disabled={!navigation.nextTimestepId}
          >
            Next
          </Button>
        </Group>
      </Group>

      <div>
        <Group gap="sm" mb="xs">
          <Text size="sm" c="dimmed" fw={600}>
            Timestamp:
          </Text>
          <Text size="sm" ff="monospace">
            {timestep.formattedTimestamp}
          </Text>
        </Group>
        <Group gap="sm">
          <Text size="sm" c="dimmed" fw={600}>
            URL:
          </Text>
          <Text
            size="sm"
            ff="monospace"
            fw={600}
            style={{
              wordBreak: "break-all",
            }}
          >
            {timestep.url}
          </Text>
        </Group>
      </div>
    </Stack>
  );
};
